import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { resetPassword, selectAuthStatus, selectAuthError, clearError } from '../features/auth/authSlice';
import SEO from './SEO';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setTokenMissing(true);
    }
  }, [searchParams]);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    dispatch(clearError());

    // Validate new password
    const pwdError = validatePassword(newPassword);
    if (pwdError) {
      setValidationError(pwdError);
      return;
    }

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    try {
      await dispatch(resetPassword({ token, newPassword })).unwrap();
      setResetSuccess(true);
    } catch (err) {
      console.error('Reset password failed:', err);
    }
  };

  // Token missing view
  if (tokenMissing) {
    return (
      <>
        <SEO
          title="Invalid Reset Link - myCleanStudio"
          description="The password reset link is invalid or missing"
          url="/reset-password"
        />
        <div className="reset-password-page">
          <div className="reset-password-container">
            <div className="reset-password-header">
              <div className="reset-password-icon error-icon">
                <AlertCircle size={32} color="white" />
              </div>
              <h2 className="reset-password-title">Invalid reset link</h2>
              <p className="reset-password-subtitle">
                This password reset link is invalid or has expired.
              </p>
            </div>

            <div className="reset-password-actions">
              <Link to="/forgot-password" className="primary-btn">
                Request a new reset link
              </Link>

              <Link to="/login" className="secondary-btn">
                <ArrowLeft size={16} />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Success view
  if (resetSuccess) {
    return (
      <>
        <SEO
          title="Password Reset Successful - myCleanStudio"
          description="Your password has been reset successfully"
          url="/reset-password"
        />
        <div className="reset-password-page">
          <div className="reset-password-container">
            <div className="reset-password-header">
              <div className="reset-password-icon success-icon">
                <CheckCircle size={32} color="white" />
              </div>
              <h2 className="reset-password-title">Password reset successful!</h2>
              <p className="reset-password-subtitle">
                Your password has been changed and you're now signed in.
              </p>
            </div>

            <div className="reset-password-actions">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="primary-btn"
              >
                Go to dashboard
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Reset form view
  return (
    <>
      <SEO
        title="Reset Password - myCleanStudio"
        description="Create a new password for your myCleanStudio account"
        url="/reset-password"
      />
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-header">
            <div className="reset-password-icon">
              <Lock size={32} color="white" />
            </div>
            <h2 className="reset-password-title">Create new password</h2>
            <p className="reset-password-subtitle">
              Enter your new password below.
            </p>
          </div>

          {(error || validationError) && (
            <div className="error-message">
              {validationError || error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="reset-password-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  disabled={status === 'loading'}
                  autoFocus
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="password-hint">
                Must include uppercase, lowercase, and a number
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  disabled={status === 'loading'}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <>
                  <span className="btn-spinner"></span>
                  Resetting password...
                </>
              ) : (
                'Reset password'
              )}
            </button>
          </form>

          <div className="reset-password-footer">
            <Link to="/login" className="go-back-btn">
              <ArrowLeft size={16} />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
